import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';
import { useCampaignReports } from '../CampaignReportsContext';
import EngagementSurveyReportEditCustomModalEditor from './EngagementSurveyReportEditCustomModalEditor';
import EngagementSurveyReportEditModalEditor from './EngagementSurveyReportEditModalEditor';
import EngagementSurveyReportNotificationModalEditor from './EngagementSurveyReportNotificationModalEditor';

interface EditModalProps {
  reportId: number;
  sliceType?: string;
  isOpen: boolean;
  onClosed: (hasPublishedItems: boolean) => void;
  onCallback: (hasPublishedItems: boolean) => void;
}

const EditModal: FC<EditModalProps> = ({
  reportId,
  sliceType,
  onClosed,
  onCallback,
  isOpen,
}) => {
  return (
    <>
      {sliceType !== 'CUSTOM' && (
        <EngagementSurveyReportEditModalEditor
          reportId={reportId}
          isOpen={isOpen}
          onClosed={onClosed}
          onCallback={onCallback}
        />
      )}
      {sliceType === 'CUSTOM' && (
        <EngagementSurveyReportEditCustomModalEditor
          reportId={reportId}
          isOpen={isOpen}
          onClosed={onClosed}
          onCallback={onCallback}
        />
      )}
    </>
  );
};

interface EditButtonProps {
  reportId: number;
  sliceType?: string;
}

const EditButton: FC<EditButtonProps> = ({ reportId, sliceType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const { refreshEngagementSurveyListVersion } = useCampaignReports();

  const handleOnClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleOnClosed = useCallback((_hasPublishedItems: boolean) => {
    setIsOpen(false);
  }, []);

  const handleNotificationClosed = useCallback(() => {
    setIsNotifyOpen(false);
    // Refresh when notification modal is closed explicitly
    refreshEngagementSurveyListVersion();
  }, [refreshEngagementSurveyListVersion]);

  // Handle callback from the edit modal
  const handleOnCallback = useCallback(
    (hasPublishedItems: boolean) => {
      // Close the edit modal first
      setIsOpen(false);

      // If there are published items, open the notification modal
      if (hasPublishedItems) {
        setIsNotifyOpen(true);
      } else {
        // Otherwise just refresh the list
        refreshEngagementSurveyListVersion();
      }
    },
    [refreshEngagementSurveyListVersion]
  );

  const handleNotificationCallback = useCallback(() => {
    setIsNotifyOpen(false);
  }, []);

  return (
    <Button className="ms-3 btn btn-light btn-sm" onClick={handleOnClick}>
      <FormattedMessage
        id="app.views.administration.campaign_reports.engagement_survey.engagement_survey_reports_table.edit"
        defaultMessage="Edit"
      />
      <EditModal
        reportId={reportId}
        sliceType={sliceType}
        isOpen={isOpen}
        onClosed={handleOnClosed}
        onCallback={handleOnCallback}
      />
      <EngagementSurveyReportNotificationModalEditor
        reportId={reportId}
        isOpen={isNotifyOpen}
        onClosed={handleNotificationClosed}
        onCallback={handleNotificationCallback}
      />
    </Button>
  );
};

const MemoizedEditButton = React.memo(EditButton);
const MemoizedEditModal = React.memo(EditModal);

export { MemoizedEditButton as EditButton, MemoizedEditModal as EditModal };
